/*
 * LingGro smooth instant bulk cart for Dawn
 *
 * Behaviour:
 * - Every +/- change is a real cart change (not a local selection list).
 * - The quantity shown on the product card changes immediately.
 * - The header cart count changes immediately, without bump/scale animation.
 * - Positive changes are coalesced for a short quiet window and sent with /cart/add.js in small batches.
 * - Cart writes are serialized to prevent overlapping requests from overwriting each other.
 * - Decreases use /cart/change.js, as recommended by Shopify for existing lines.
 * - Product cards are never re-rendered after a click, so there is no card/quantity jump.
 * - Cart writes do not request sections and collection clicks do not perform extra section/cart reads.
 * - Temporary verification/429/network failures NEVER roll selected quantities back; they cool down, reconcile once, and retry.
 */

(() => {
  // Give rapid clicks a slightly longer quiet window so many + actions can be
  // combined into one Shopify request. This greatly reduces abuse/challenge triggers.
  const FLUSH_DELAY = 700;
  const ADD_BATCH_SIZE = 40;
  const MAX_RETRIES = 2;
  const RETRY_BASE_DELAY = 1200;
  const DEFERRED_RETRY_BASE_DELAY = 6000;
  const DEFERRED_RETRY_MAX_DELAY = 30000;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class LingGroInstantCartManager {
    constructor() {
      this.components = new Set();
      this.serverCount = this.readInitialCartCount();
      this.processing = false;
      this.flushTimer = null;
      this.needsAnotherFlush = false;
      this.deferredRetryTimer = null;
      this.deferredRetryAttempt = 0;
      this.cooldownUntil = 0;
    }

    register(component) {
      this.components.add(component);
      this.renderCartCount();
    }

    unregister(component) {
      this.components.delete(component);
    }

    readInitialCartCount() {
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (!bubble) return 0;

      const fromData = Number.parseInt(bubble.dataset.cartCount, 10);
      if (Number.isFinite(fromData)) return fromData;

      const visible = bubble.querySelector('[data-cart-count-value], [aria-hidden="true"]');
      const fromText = Number.parseInt((visible?.textContent || '').replace(/[^0-9-]/g, ''), 10);
      return Number.isFinite(fromText) ? fromText : 0;
    }

    getPendingDelta() {
      let pending = 0;
      this.components.forEach((component) => {
        pending += component.desiredQty - component.confirmedQty;
      });
      return pending;
    }

    renderCartCount() {
      const count = Math.max(0, this.serverCount + this.getPendingDelta());
      const cartIcon = document.querySelector('#cart-icon-bubble');
      if (!cartIcon) return;

      // Never animate/bump the icon for list +/- changes.
      cartIcon.classList.remove('lg-cart-bump');

      let bubble = cartIcon.querySelector('.cart-count-bubble');
      if (count <= 0) {
        bubble?.remove();
        return;
      }

      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true" data-cart-count-value></span><span class="visually-hidden" data-cart-count-a11y></span>';
        cartIcon.appendChild(bubble);
      }

      bubble.dataset.cartCount = String(count);

      let visible = bubble.querySelector('[data-cart-count-value]');
      if (!visible) {
        visible = bubble.querySelector('[aria-hidden="true"]');
      }
      if (!visible) {
        visible = document.createElement('span');
        visible.setAttribute('aria-hidden', 'true');
        bubble.prepend(visible);
      }
      visible.dataset.cartCountValue = '';
      visible.textContent = String(count);

      let a11y = bubble.querySelector('[data-cart-count-a11y]');
      if (!a11y) a11y = bubble.querySelector('.visually-hidden');
      if (!a11y) {
        a11y = document.createElement('span');
        a11y.className = 'visually-hidden';
        bubble.appendChild(a11y);
      }
      a11y.dataset.cartCountA11y = '';
      a11y.textContent = `${count} item${count === 1 ? '' : 's'} in cart`;
    }

    scheduleFlush() {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;

      // If Shopify has challenged/throttled the session, do not keep hammering it.
      // New clicks stay visible and queued until the cooldown retry runs.
      if (this.deferredRetryTimer) return;

      const cooldownLeft = Math.max(0, this.cooldownUntil - Date.now());
      this.flushTimer = setTimeout(() => this.flush(), Math.max(FLUSH_DELAY, cooldownLeft));
    }

    async waitUntilIdle() {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;

      // Pagination waits for the real cart sync. During a verification cooldown we
      // wait for the scheduled retry instead of sending duplicate requests.
      while (this.processing || this.hasPendingChanges()) {
        if (this.deferredRetryTimer) {
          await sleep(120);
          continue;
        }
        if (!this.processing) await this.flush();
        if (this.processing) await sleep(30);
      }
    }

    hasPendingChanges() {
      return Array.from(this.components).some(
        (component) => component.isConnected && component.desiredQty !== component.confirmedQty
      );
    }

    snapshotPending() {
      return Array.from(this.components)
        .filter((component) => component.isConnected && component.desiredQty !== component.confirmedQty)
        .map((component) => ({
          component,
          id: component.variantId,
          from: component.confirmedQty,
          target: component.desiredQty,
          delta: component.desiredQty - component.confirmedQty,
        }));
    }

    async flush() {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;

      if (this.deferredRetryTimer || Date.now() < this.cooldownUntil) return;

      if (this.processing) {
        this.needsAnotherFlush = true;
        return;
      }

      this.processing = true;
      let deferred = false;

      try {
        // Keep draining until every click that happened while requests were in flight
        // has reached Shopify. No arbitrary product/design count is imposed here.
        while (this.hasPendingChanges() && !deferred) {
          const snapshot = this.snapshotPending();
          const additions = snapshot.filter((entry) => entry.delta > 0);

          if (additions.length) {
            for (let i = 0; i < additions.length; i += ADD_BATCH_SIZE) {
              const result = await this.addBatchResilient(additions.slice(i, i + ADD_BATCH_SIZE));
              if (result === 'deferred') {
                deferred = true;
                break;
              }
            }
          }

          if (deferred) break;

          // Re-read after additions because the customer may have clicked again
          // while the add request was running.
          const reductions = this.snapshotPending().filter((entry) => entry.delta < 0);
          for (const entry of reductions) {
            const result = await this.changeOne(entry);
            if (result === 'deferred') {
              deferred = true;
              break;
            }
          }
        }
      } finally {
        this.processing = false;
        if (!this.deferredRetryTimer && (this.needsAnotherFlush || this.hasPendingChanges())) {
          this.needsAnotherFlush = false;
          this.scheduleFlush();
        }
      }
    }

    sanitizeErrorMessage(value) {
      const fallback = 'Cart update was interrupted. Please try again.';
      if (!value) return fallback;
      const text = String(value)
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!text) return fallback;
      if (/verify|verif|connection|cloudflare|challenge|enable javascript|cookies/i.test(text)) {
        return 'Connection verification interrupted the cart update. Retrying may help.';
      }
      return text.slice(0, 180);
    }

    isChallengeLikeResponse(response, text) {
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      if (contentType.includes('text/html')) return true;
      const sample = (text || '').slice(0, 12000).toLowerCase();
      return (
        sample.startsWith('<!doctype html') ||
        sample.startsWith('<html') ||
        sample.includes('verify your connection') ||
        sample.includes('connection needs to be verified') ||
        sample.includes('enable javascript and cookies') ||
        sample.includes('cf-chl-') ||
        sample.includes('challenge-platform') ||
        sample.includes('cloudflare')
      );
    }

    async request(url, body, attempt = 0) {
      let response;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        });
      } catch (networkError) {
        if (attempt < MAX_RETRIES) {
          await sleep(RETRY_BASE_DELAY * 2 ** attempt + Math.floor(Math.random() * 250));
          return this.request(url, body, attempt + 1);
        }
        const error = new Error('Network interrupted the cart update. Please try again.');
        error.status = 0;
        throw error;
      }

      const text = await response.text();
      let data = {};
      let parsed = false;
      if (text) {
        try {
          data = JSON.parse(text);
          parsed = true;
        } catch (_error) {
          parsed = false;
        }
      } else {
        parsed = true;
      }

      const challengeLike = this.isChallengeLikeResponse(response, text);
      const retryable = challengeLike || response.status === 429 || response.status >= 500;

      // A verification/challenge page can occasionally arrive with HTTP 200.
      // Never treat HTML as a successful cart response and never display its source code.
      if (retryable && attempt < MAX_RETRIES) {
        const retryAfter = Number.parseFloat(response.headers.get('Retry-After'));
        const retryAfterMs = Number.isFinite(retryAfter) ? retryAfter * 1000 : 0;
        const backoff = Math.max(retryAfterMs, RETRY_BASE_DELAY * 2 ** attempt) + Math.floor(Math.random() * 300);
        await sleep(backoff);
        return this.request(url, body, attempt + 1);
      }

      if (response.ok && parsed && !challengeLike) return data;

      const message = challengeLike
        ? 'Connection verification interrupted the cart update. Please try again.'
        : this.sanitizeErrorMessage(data?.description || data?.message || text || `Cart request failed (${response.status})`);
      const error = new Error(message);
      error.status = challengeLike && response.ok ? 503 : response.status;
      error.data = parsed ? data : null;
      error.challengeLike = challengeLike;
      throw error;
    }

    getAddUrl() {
      const root = window.Shopify?.routes?.root || '/';
      return window.routes?.cart_add_url || `${root}cart/add.js`;
    }

    getChangeUrl() {
      const root = window.Shopify?.routes?.root || '/';
      return window.routes?.cart_change_url || `${root}cart/change.js`;
    }

    extractCountFromSection(html) {
      if (!html) return null;
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const bubble = doc.querySelector('.cart-count-bubble');
        if (!bubble) return 0;
        const count = Number.parseInt(bubble.dataset.cartCount, 10);
        if (Number.isFinite(count)) return count;
        const text = bubble.querySelector('[data-cart-count-value], [aria-hidden="true"]')?.textContent || '';
        const parsed = Number.parseInt(text.replace(/[^0-9-]/g, ''), 10);
        return Number.isFinite(parsed) ? parsed : null;
      } catch (_error) {
        return null;
      }
    }

    getCartUrl() {
      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart.js`;
    }

    isTransientError(error) {
      return !!(
        error?.challengeLike ||
        error?.status === 0 ||
        error?.status === 429 ||
        error?.status >= 500
      );
    }

    scheduleDeferredRetry() {
      if (this.deferredRetryTimer) return;

      const exponent = Math.min(this.deferredRetryAttempt, 3);
      const delay = Math.min(
        DEFERRED_RETRY_MAX_DELAY,
        DEFERRED_RETRY_BASE_DELAY * 2 ** exponent
      ) + Math.floor(Math.random() * 700);

      this.deferredRetryAttempt += 1;
      this.cooldownUntil = Date.now() + delay;

      this.deferredRetryTimer = setTimeout(async () => {
        this.deferredRetryTimer = null;
        this.cooldownUntil = 0;

        // One authoritative read before retrying prevents a duplicate add if Shopify
        // accepted the previous POST but its response was interrupted.
        const reconciled = await this.reconcileFromCart();
        if (!reconciled) {
          this.scheduleDeferredRetry();
          return;
        }

        if (this.hasPendingChanges()) {
          this.flush();
        }
      }, delay);
    }

    async reconcileFromCart() {
      let response;
      try {
        response = await fetch(this.getCartUrl(), {
          method: 'GET',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          cache: 'no-store',
        });
      } catch (_error) {
        return false;
      }

      const text = await response.text();
      if (!response.ok || this.isChallengeLikeResponse(response, text)) return false;

      let cart;
      try {
        cart = JSON.parse(text);
      } catch (_error) {
        return false;
      }

      if (!cart || !Array.isArray(cart.items)) return false;

      const quantities = new Map();
      cart.items.forEach((item) => {
        const id = Number(item.variant_id);
        if (!Number.isFinite(id)) return;
        quantities.set(id, (quantities.get(id) || 0) + (Number.parseInt(item.quantity, 10) || 0));
      });

      const authoritativeCount = Number.parseInt(cart.item_count, 10);
      if (Number.isFinite(authoritativeCount)) this.serverCount = authoritativeCount;

      this.components.forEach((component) => {
        if (!component.isConnected) return;
        const actual = quantities.get(component.variantId) || 0;
        const wanted = component.desiredQty;
        component.commitConfirmed(actual);

        // If another script/app raised the real cart quantity above this card's
        // optimistic target, show the real value rather than scheduling a reduction.
        if (actual > wanted) {
          component.desiredQty = actual;
          if (component.input) component.input.value = String(actual);
        }
      });

      this.renderCartCount();
      this.deferredRetryAttempt = 0;
      return true;
    }

    async addBatch(entries) {
      // Snapshot only the unsynced positive amount. If the customer clicks again
      // during the request, that newer delta remains pending for the next pass.
      const liveEntries = entries.filter(
        (entry) => entry.component.isConnected && entry.component.confirmedQty === entry.from && entry.delta > 0
      );
      if (!liveEntries.length) return;

      const body = {
        items: liveEntries.map((entry) => ({ id: entry.id, quantity: entry.delta })),
      };

      await this.request(this.getAddUrl(), body);

      let confirmedDelta = 0;
      liveEntries.forEach((entry) => {
        const nextConfirmed = entry.from + entry.delta;
        confirmedDelta += entry.delta;
        entry.component.commitConfirmed(nextConfirmed);
      });

      this.serverCount += confirmedDelta;
      this.renderCartCount();
      this.deferredRetryAttempt = 0;
    }

    async addBatchResilient(entries) {
      if (!entries.length) return 'ok';

      try {
        await this.addBatch(entries);
        return 'ok';
      } catch (error) {
        // Verification / throttling / network failures are temporary. Keep every
        // clicked quantity exactly as the customer selected it and retry later.
        // NEVER roll these quantities back to zero.
        if (this.isTransientError(error)) {
          this.scheduleDeferredRetry();
          return 'deferred';
        }

        // One unavailable/invalid variant should never block all surrounding products.
        if (entries.length > 1 && error?.status >= 400 && error?.status < 500) {
          const middle = Math.ceil(entries.length / 2);
          const left = await this.addBatchResilient(entries.slice(0, middle));
          if (left === 'deferred') return 'deferred';
          return this.addBatchResilient(entries.slice(middle));
        }

        if (entries.length === 1) {
          // Only a permanent item-specific error (unavailable/invalid/inventory rule)
          // is allowed to undo that one card. Transient connection errors never do.
          entries[0].component.rollbackToConfirmed(error?.message || 'This item could not be added.');
          this.renderCartCount();
        }
        return 'ok';
      }
    }

    async changeOne(entry) {
      const component = entry.component;
      if (!component.isConnected || component.confirmedQty !== entry.from || component.desiredQty === component.confirmedQty) return 'ok';

      const target = component.desiredQty;
      const oldConfirmed = component.confirmedQty;

      try {
        const data = await this.request(this.getChangeUrl(), {
          id: entry.id,
          quantity: target,
        });

        const matchingItems = Array.isArray(data?.items)
          ? data.items.filter((item) => Number(item.variant_id) === entry.id)
          : [];
        const actual = matchingItems.reduce((sum, item) => sum + (Number.parseInt(item.quantity, 10) || 0), 0);
        const nextConfirmed = matchingItems.length ? actual : target;

        const authoritativeCount = Number.parseInt(data?.item_count, 10);
        if (Number.isFinite(authoritativeCount)) {
          this.serverCount = authoritativeCount;
        } else {
          this.serverCount += nextConfirmed - oldConfirmed;
        }

        component.commitConfirmed(nextConfirmed);
        this.renderCartCount();
        this.deferredRetryAttempt = 0;
        return 'ok';
      } catch (error) {
        if (this.isTransientError(error)) {
          this.scheduleDeferredRetry();
          return 'deferred';
        }
        component.rollbackToConfirmed(error?.message || 'This quantity could not be updated.');
        this.renderCartCount();
        return 'ok';
      }
    }
  }

  function getManager() {
    if (!window.LingGroInstantCart) {
      window.LingGroInstantCart = new LingGroInstantCartManager();
    }
    return window.LingGroInstantCart;
  }

  if (!customElements.get('quick-add-bulk')) {
    customElements.define(
      'quick-add-bulk',
      class QuickAddBulk extends BulkAdd {
        constructor() {
          super();
          this.quantity = this.querySelector('quantity-input');
          this._onChange = this.onQuantityChange.bind(this);
          this._selectInput = (event) => event.target.select();
          this._blurOnEnter = (event) => {
            if (event.key === 'Enter') event.target.blur();
          };

          const initial = Number.parseInt(this.input?.dataset.cartQuantity ?? this.input?.value ?? '0', 10);
          this.confirmedQty = Number.isFinite(initial) ? initial : 0;
          this.desiredQty = this.confirmedQty;
          this._lastErrorToastAt = 0;
        }

        connectedCallback() {
          this.addEventListener('change', this._onChange);
          this.input?.addEventListener('focusin', this._selectInput);
          this.input?.addEventListener('keydown', this._blurOnEnter);
          getManager().register(this);
        }

        disconnectedCallback() {
          this.removeEventListener('change', this._onChange);
          this.input?.removeEventListener('focusin', this._selectInput);
          this.input?.removeEventListener('keydown', this._blurOnEnter);
          getManager().unregister(this);
        }

        get input() {
          return this.querySelector('quantity-input input.quantity__input');
        }

        get variantId() {
          return Number.parseInt(this.dataset.index, 10);
        }

        normalizeQuantity(input) {
          let quantity = Number.parseInt(input.value, 10);
          if (!Number.isFinite(quantity) || quantity < 0) quantity = 0;

          const min = Number.parseInt(input.dataset.min, 10) || 1;
          const step = Number.parseInt(input.step, 10) || 1;
          const max = input.max ? Number.parseInt(input.max, 10) : null;

          if (quantity > 0 && quantity < min) quantity = min;
          if (quantity > 0 && step > 1) {
            const offset = Math.max(0, quantity - min);
            if (offset % step !== 0) quantity = min + Math.ceil(offset / step) * step;
          }
          if (Number.isFinite(max)) quantity = Math.min(quantity, max);

          return Math.max(0, quantity);
        }

        onQuantityChange(event) {
          const input = event.target.closest?.('input.quantity__input');
          if (!input || !this.contains(input) || !Number.isFinite(this.variantId)) return;

          const quantity = this.normalizeQuantity(input);
          input.value = String(quantity);
          input.setCustomValidity('');
          input.reportValidity();
          this.desiredQty = quantity;
          this.quantity?.validateQtyRules?.();

          const manager = getManager();
          manager.renderCartCount();
          manager.scheduleFlush();
        }

        commitConfirmed(quantity) {
          const qty = Math.max(0, Number.parseInt(quantity, 10) || 0);
          this.confirmedQty = qty;
          if (this.input) {
            this.input.dataset.cartQuantity = String(qty);
            this.input.setAttribute('value', String(qty));
          }
        }

        rollbackToConfirmed(message) {
          this.desiredQty = this.confirmedQty;
          if (this.input) {
            this.input.value = String(this.confirmedQty);
            this.input.dataset.cartQuantity = String(this.confirmedQty);
            this.input.setAttribute('value', String(this.confirmedQty));
            this.quantity?.validateQtyRules?.();
          }
          this.showError(message);
        }

        showError(message) {
          const now = Date.now();
          if (now - this._lastErrorToastAt < 1200) return;
          this._lastErrorToastAt = now;

          document.getElementById('LingGroInstantCartToast')?.remove();
          const toast = document.createElement('div');
          toast.id = 'LingGroInstantCartToast';
          toast.setAttribute('role', 'status');
          toast.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:99999;max-width:min(92vw,520px);padding:10px 14px;border-radius:8px;background:#111;color:#fff;font-size:14px;line-height:1.35;box-shadow:0 4px 18px rgba(0,0,0,.18)';
          toast.textContent = getManager().sanitizeErrorMessage(message || 'This item could not be updated.');
          document.body.appendChild(toast);
          setTimeout(() => toast.remove(), 3500);
        }

        // Dawn BulkAdd writes with /cart/update.js and re-renders product-card sections.
        // This implementation owns cart synchronization, so the inherited queue is disabled.
        startQueue() {}
        sendRequest() {}
        updateMultipleQty() {}
      }
    );
  }

  // When the customer goes to the next/previous collection page, finish any
  // just-clicked cart writes first. This prevents a fast pagination click from
  // aborting the last AJAX request during navigation.
  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('.pagination-wrapper a[href]');
      if (!link || link.target === '_blank') return;

      const manager = window.LingGroInstantCart;
      if (!manager || (!manager.processing && !manager.hasPendingChanges())) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const href = link.href;
      manager.waitUntilIdle().finally(() => window.location.assign(href));
    },
    true
  );
})();
